import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, CalendarOff, Check, X, ArrowUp } from "lucide-react";
import api from "../constant/api";
import { toast } from "react-toastify";
import { useMenuPermission } from "../hooks/useMenuPermission";

const MENU_ID = 23;
const LEAVE_TYPES = ["casual", "sick", "earned", "unpaid"];
const ITEMS_PER_PAGE = 5;

export default function Leave() {
  const { loading } = useMenuPermission(MENU_ID);
  const email = localStorage.getItem("userEmail");
  const role = localStorage.getItem("userRole");
  console.log("role",role)
 const user = JSON.parse(localStorage.getItem("user"));

const user_id = user?.user_id;
const name = user?.name;

  const isAdmin = role === "admin" || role === "superadmin";

  const [leaves, setLeaves] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showAdd, setShowAdd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [form, setForm] = useState({ leave_type: "casual", from_date: "", to_date: "", reason: "" });


const load = useCallback(() => {
  api.get("/Salesman/getLeaves", {
    params: {
      user_id: user_id,
      role: role,
    },
  })
    .then((res) => {
      if (res.data.success) {
        setLeaves(res.data.data || []);
      }
    })
    .catch((err) => {
      console.log(err);
    });
}, [user_id,role]);

  useEffect(() => {
    load();
    const handleScroll = () => setScrollTop(window.pageYOffset);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [load]);

  const handleAdd = () => {
    if (!form.from_date || !form.to_date) return toast.error("Select both dates");
    setBusy(true);
    api.post("/Salesman/addLeave", { role:role,user_id : user_id ,name: name, ...form })
      .then(res => {
        if (res.data.success) {
          toast.success("Leave request submitted ✅");
          setShowAdd(false);
          setForm({ leave_type: "casual", from_date: "", to_date: "", reason: "" });
          load();
        } else {
          toast.error(res.data.msg);
        }
      })
      .catch(() => toast.error("Failed to submit"))
      .finally(() => setBusy(false));
  };

  const handleDecision = (id, status) => {
  const user = JSON.parse(localStorage.getItem("user"));

  api.put(`/Salesman/decideLeave/${id}`, {
    user_id: user.user_id,
    status,
  })
    .then((res) => {
      if (res.data.success) {
        toast.success(`Leave ${status}`);
        load();
      } else {
        toast.error(res.data.msg);
      }
    })
    .catch((err) => {
      console.log(err);
      toast.error("Failed to update");
    });
};

  const statusColor = (s) => s === "approved" ? "bg-green-100 text-green-700 border-green-200" : s === "rejected" ? "bg-red-100 text-red-700 border-red-200" : "bg-amber-100 text-amber-700 border-amber-200";

  const days = (from, to) => Math.round((new Date(to) - new Date(from)) / 86400000) + 1;

  const filtered = leaves.filter((l) => 
    l.leave_type?.toLowerCase().includes(search.toLowerCase()) || 
    l.salesman_name?.toLowerCase().includes(search.toLowerCase()) ||
    l.reason?.toLowerCase().includes(search.toLowerCase()) ||
    l.status?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE) || 1;
  const currentData = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <p className="text-gray-500 font-medium">Checking permissions...</p>
      </div>
    );
  }

  return (
    <div className="p-3 md:p-6 min-h-screen bg-gray-50/50">

      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Leave</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isAdmin ? "Approve or reject leave requests" : "Apply for leave and track status"}
          </p>
        </div>

        <button
          onClick={() => setShowAdd(true)}
          className="hidden md:flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl shadow transition"
        >
          <Plus size={16} /> Apply Leave
        </button>
      </div>

      {/* SEARCH BAR & CONTROLS */}
      <div className="bg-white border border-gray-100 p-4 rounded-2xl shadow-sm mb-6 flex flex-col md:flex-row gap-3 items-center justify-between">
        <div className="w-full md:w-80">
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            placeholder="Search leaves, type, salesman..."
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50/50"
          />
        </div>
      </div>

      <button
        onClick={() => setShowAdd(true)}
        className="fixed bottom-6 right-5 bg-blue-600 hover:bg-blue-700 text-white w-14 h-14 flex items-center justify-center rounded-full shadow-2xl md:hidden z-40 active:scale-95 transition"
      >
        <Plus size={20} />
      </button>

      {/* DESKTOP TABLE */}
      <div className="hidden md:block overflow-x-auto bg-white rounded-2xl shadow-sm border border-gray-100 mb-6">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-900 text-white">
            <tr>
              {isAdmin && <th className="px-4 py-3.5 text-left">Salesman</th>}
              <th className="px-4 py-3.5 text-left">Type</th>
              <th className="px-4 py-3.5 text-left">From</th>
              <th className="px-4 py-3.5 text-left">To</th>
              <th className="px-4 py-3.5 text-center">Days</th>
              <th className="px-4 py-3.5 text-left">Reason</th>
              <th className="px-4 py-3.5 text-center">Status</th>
              {isAdmin && <th className="px-4 py-3.5 text-center">Action</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {currentData.map((l) => (
              <tr key={l.id} className="hover:bg-gray-50/50 transition">
                {isAdmin && <td className="px-4 py-3.5 font-medium text-gray-800">{l.name}</td>}
                <td className="px-4 py-3.5 capitalize font-semibold text-gray-700">{l.leave_type}</td>
                <td className="px-4 py-3.5 text-gray-600">{new Date(l.from_date).toLocaleDateString("en-IN")}</td>
                <td className="px-4 py-3.5 text-gray-600">{new Date(l.to_date).toLocaleDateString("en-IN")}</td>
                <td className="px-4 py-3.5 text-center font-medium">{days(l.from_date, l.to_date)}</td>
                <td className="px-4 py-3.5 text-gray-600 max-w-xs truncate">{l.reason || "—"}</td>
                <td className="px-4 py-3.5 text-center">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${statusColor(l.status)}`}>{l.status}</span>
                </td>
                {isAdmin && (
                  <td className="px-4 py-3.5 text-center">
                    {l.status === "pending" ? (
                      <div className="flex justify-center gap-2">
                        <button onClick={() => handleDecision(l.leave_id, "approved")} className="bg-green-500 hover:bg-green-600 text-white p-1.5 rounded-lg shadow-sm"><Check size={14} /></button>
                        <button onClick={() => handleDecision(l.leave_id, "rejected")} className="bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-lg shadow-sm"><X size={14} /></button>
                      </div>
                    ) : "—"}
                  </td>
                )}
              </tr>
            ))}
            {currentData.length === 0 && (
              <tr><td colSpan={isAdmin ? 8 : 6} className="text-center py-12 text-gray-400">No leave requests found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* MOBILE CARDS */}
      <div className="md:hidden space-y-3 pb-24">
        {currentData.map((l) => (
          <motion.div key={l.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-gray-100 rounded-2xl shadow-sm p-4">
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="font-bold text-gray-800 text-base capitalize">{l.leave_type} Leave</p>
                {isAdmin && <p className="text-xs font-medium text-gray-500">{l.name}</p>}
                <p className="text-xs text-gray-400 mt-0.5">
                  {new Date(l.from_date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} – {new Date(l.to_date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} ({days(l.from_date, l.to_date)}d)
                </p>
              </div>
              <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${statusColor(l.status)}`}>{l.status}</span>
            </div>

            {l.reason && <p className="text-xs text-gray-600 mt-2 bg-gray-50 p-2.5 rounded-xl">{l.reason}</p>}

            {isAdmin && l.status === "pending" && (
              <div className="flex gap-2 mt-3 pt-2 border-t border-gray-100">
                <button onClick={() => handleDecision(l.leave_id, "approved")} className="flex-1 bg-green-500 text-white py-2 rounded-xl text-xs font-semibold flex items-center justify-center gap-1 shadow-sm"><Check size={13} /> Approve</button>
                <button onClick={() => handleDecision(l.leave_id, "rejected")} className="flex-1 bg-red-500 text-white py-2 rounded-xl text-xs font-semibold flex items-center justify-center gap-1 shadow-sm"><X size={13} /> Reject</button>
              </div>
            )}
          </motion.div>
        ))}
        {currentData.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <CalendarOff className="mx-auto mb-2" size={32} />
            No leave requests found.
          </div>
        )}
      </div>

      {/* PAGINATION */}
      {filtered.length > 0 && (
        <div className="flex justify-center items-center gap-2 mt-6 mb-12">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
            className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-xs font-semibold disabled:opacity-40 shadow-sm"
          >
            Previous
          </button>
          <span className="text-xs font-medium text-gray-600">Page {currentPage} of {totalPages}</span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
            className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-xs font-semibold disabled:opacity-40 shadow-sm"
          >
            Next
          </button>
        </div>
      )}

      {/* APPLY LEAVE MODAL */}
      <AnimatePresence>
        {showAdd && (
          <>
            <motion.div className="fixed inset-0 bg-black/50 z-50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowAdd(false)} />
            <motion.div
              className="fixed inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center z-50"
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 250 }}
            >
              <div className="bg-white w-full sm:max-w-md sm:mx-4 rounded-t-[28px] sm:rounded-2xl shadow-2xl max-h-[92vh] sm:max-h-[85vh] flex flex-col overflow-hidden">
                <div className="flex justify-center pt-3 sm:hidden shrink-0"><div className="w-14 h-1.5 bg-gray-300 rounded-full" /></div>
                <div className="px-5 pt-4 pb-3 border-b border-gray-100 flex justify-between items-center shrink-0">
                  <h2 className="text-lg font-bold text-gray-800">Apply for Leave</h2>
                  <button onClick={() => setShowAdd(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                </div>

                <div className="p-5 space-y-3.5 overflow-y-auto flex-1">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block font-medium">Leave Type</label>
                    <select className="border border-gray-200 w-full p-3 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white" value={form.leave_type} onChange={e => setForm({ ...form, leave_type: e.target.value })}>
                      {LEAVE_TYPES.map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block font-medium">From Date</label>
                      <input type="date" value={form.from_date} onChange={e => setForm({ ...form, from_date: e.target.value })} className="border border-gray-200 w-full p-3 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block font-medium">To Date</label>
                      <input type="date" value={form.to_date} onChange={e => setForm({ ...form, to_date: e.target.value })} className="border border-gray-200 w-full p-3 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 mb-1 block font-medium">Reason (optional)</label>
                    <textarea rows={3} placeholder="Enter reason..." value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} className="border border-gray-200 w-full p-3 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none" />
                  </div>
                </div>

                <div className="flex gap-2 p-4 border-t border-gray-100 shrink-0 bg-white">
                  <button onClick={handleAdd} disabled={busy} className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-semibold text-sm active:scale-95 transition disabled:opacity-50 shadow-sm">
                    {busy ? "Submitting…" : "Submit Request"}
                  </button>
                  <button onClick={() => setShowAdd(false)} className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold text-sm active:scale-95 transition">Cancel</button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* SCROLL TO TOP */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: scrollTop > 200 ? 1 : 0 }}
        whileHover={{ scale: 1.05 }}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className="fixed bottom-24 right-5 md:bottom-8 md:right-8 w-11 h-11 bg-white border border-gray-200 text-gray-700 rounded-full shadow-lg flex items-center justify-center z-30"
      >
        <ArrowUp size={18} />
      </motion.button>

    </div>
  );
}